import { browser } from "$app/environment";
import { identity } from "$crypto/identity.svelte";
import { profile } from "$stores/profile.svelte";

/**
 * Cross-screen invite + result share helpers.
 *
 * Routes through `navigator.share` when available (iOS / Android share sheet)
 * and falls back to `navigator.clipboard.writeText`. Used by Bond / Intro /
 * Share so the share text and URL stay consistent across the app.
 */

export interface ShareOutcome {
  ok: boolean;
  used: "web-share" | "clipboard" | "noop";
}

/** Build the canonical bond URL with the user's pubkey + handle baked in. */
export function buildBondLink(): string {
  if (!browser) return "";
  if (!identity.publicKeyB64) return "";
  const base = `${location.origin}/`;
  const handle = encodeURIComponent(profile.handle || "");
  const tail = handle ? `&h=${handle}` : "";
  return `${base}?bond=${identity.publicKeyB64}${tail}`;
}

async function copyToClipboard(text: string): Promise<boolean> {
  if (!browser || !navigator?.clipboard) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Share the bond URL — friend can scan / open it to bond. */
export async function shareInvite(): Promise<ShareOutcome> {
  const url = buildBondLink();
  if (!url) return { ok: false, used: "noop" };

  const handle = profile.handle || "me";
  const text = `Bond with ${handle} on VuTwin`;
  const title = "VuTwin invite";

  if (browser && typeof navigator?.share === "function") {
    try {
      await navigator.share({ title, text, url });
      return { ok: true, used: "web-share" };
    } catch (err) {
      // User cancelled — not an error path. AbortError has name === 'AbortError'.
      if ((err as Error)?.name === "AbortError") {
        return { ok: false, used: "web-share" };
      }
      // fall through to clipboard
    }
  }

  const copied = await copyToClipboard(url);
  return { ok: copied, used: "clipboard" };
}

/** Share the post-pulse result — handle + twin rate + streak. */
export async function shareResult(input: {
  pulseId: string;
  syncPct: number;
  friendHandle: string;
  streak: number;
}): Promise<ShareOutcome> {
  const text = `VuTwin ${input.pulseId} — ${input.syncPct}% twin with ${input.friendHandle} | ${input.streak}d streak`;
  const url = browser ? `${location.origin}/` : "https://vutwin.app/";
  const title = "VuTwin";

  if (browser && typeof navigator?.share === "function") {
    try {
      await navigator.share({ title, text, url });
      return { ok: true, used: "web-share" };
    } catch (err) {
      if ((err as Error)?.name === "AbortError") {
        return { ok: false, used: "web-share" };
      }
    }
  }

  const copied = await copyToClipboard(`${text} - ${url}`);
  return { ok: copied, used: "clipboard" };
}
